// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import getSocialNetworkState from 'app/common/lib/react/getSocialNetworkState';
import i18n from 'app/common/lib/i18n';
import logStats from 'app/common/lib/logStats';
import SendToEmail from 'app/common/react/components/BoardEdit/SocialTypeahead/SendToEmail';
import SendToUser from 'app/common/react/components/BoardEdit/SocialTypeahead/SendToUser';
import SocialConnect from 'app/common/react/components/BoardEdit/SocialTypeahead/SocialConnect';
import SocialConnectLib from 'app/common/lib/SocialConnect';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { SocialNetworkState } from 'app/common/lib/react/getSocialNetworkState';
// eslint-disable-next-line pinterest/no-deprecated-imports
import Typeahead from 'app/common/react/ui/deprecated/Typeahead';
import { compose } from 'redux';
import { instance as pubSub } from 'app/common/lib/PubSub';
import { isEmailAddress } from 'app/common/lib/StringUtil';
import { TextField } from 'gestalt';
import { track } from 'app/common/lib/react/analytics';
import { inviteEmails, inviteUsers } from 'app/common/redux/actions/board/collab';

const DEFAULT_ALLOWED_SOCIAL_NETWORKS = [
  SocialConnectLib.FACEBOOK,
  SocialConnectLib.TWITTER,
  SocialConnectLib.GPLUS,
];

type Props = {|
  boardId?: string,
  changeOnTextHover?: boolean,
  className?: string,
  inBoardCollaboratorInviter?: boolean,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  inviteQueue?: Array<*>,
  onChange: (value: string) => void,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  onInviteItemClick: (*, *) => void,
  onKeyDown?: () => void,
  showInputPlaceholder?: boolean,
  value?: string,
  isWebMessagingRedesignExpEnabled?: boolean,
  viewer: AuthViewer,
|};

type State = {|
  networks: SocialNetworkState | null,
  open: boolean,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  typeaheadSuggestions: Array<*>,
|};

export class SocialTypeahead extends Component<Props, State> {
  static defaultProps = {
    changeOnTextHover: false,
    onKeyDown: () => {},
    showInputPlaceholder: true,
    value: '',
  };

  state = {
    networks: this.props.viewer !== undefined ? this.props.viewer.socialNetwork : null,
    open: false,
    typeaheadSuggestions: [],
  };

  previousValue: string;
  _results: ?HTMLDivElement;

  componentDidMount() {
    ['mousedown', 'touchstart'].forEach((evt) => {
      document.addEventListener(evt, this.handleBodyClick);
    });
  }

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  UNSAFE_componentWillReceiveProps({ value }: *) {
    // $FlowFixMe Flow v0.104.0 upgrade supression.
    this.setState({ open: value.length > 0 });
  }

  componentWillUnmount() {
    ['mousedown', 'touchstart'].forEach((evt) => {
      document.removeEventListener(evt, this.handleBodyClick);
    });
    this.subscriber.unsubscribe();
  }

  onUserInviteClick = (user: Object) => {
    const { id: userId, full_name: userName } = user;
    const { boardId, onInviteItemClick } = this.props;
    if (boardId) {
      onInviteItemClick(inviteUsers(boardId, [userId]), { type: 'user', userName });
    } else {
      onInviteItemClick(user, true);
    }
  };

  getStaticItems() {
    return [...this.getEmailItems(), ...this.getSocialItems(this.state.networks)];
  }

  getEmailItems() {
    const { value } = this.props;
    const email = value || '';
    return [
      <SendToEmail
        email={email}
        isValid={isEmailAddress(email)}
        key="send-to-email-component"
        onClick={this.handleOnEmailInviteClick}
        showSendEmailText={false}
      />,
    ];
  }

  getSocialItems(networks: SocialNetworkState | null): Array<SocialConnect> {
    // List of social networks the user isn't connected to
    const unconnectedNetworks = DEFAULT_ALLOWED_SOCIAL_NETWORKS.filter(
      (network) => !networks || networks[network].connected === false,
    );
    return unconnectedNetworks.map((network, idx) => (
      <SocialConnect key={`socialConnect-${network}`} network={network} />
    ));
  }

  handleBodyClick = (evt: Event) => {
    if (this._results && !this._results === evt.target) {
      this.setState({ open: false });
    }
  };

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  handleTypeaheadSuggestionsChange = (typeaheadSuggestions: Array<*>) => {
    const { viewer } = this.props;
    logStats.increment('web_contacts_search', 1, {
      source: 'social_type_ahead',
    });
    this.setState({
      typeaheadSuggestions: (typeaheadSuggestions || []).filter(
        (suggestion) => suggestion.id !== viewer.id,
      ),
    });
  };

  handleOnEmailInviteClick = () => {
    const { boardId, onInviteItemClick, value: email } = this.props;
    if (boardId && email !== undefined) {
      onInviteItemClick(inviteEmails(boardId, [email]), { type: 'email', email });
    } else {
      onInviteItemClick(email, false);
    }
  };

  subscriber = pubSub.subscribe(
    PCONST.PUBSUB_CHANNEL_CURRENT_USER,
    PCONST.PUBSUB_TOPIC_SOCIAL_NETWORK_UPDATED,
    (_, __, user) => {
      // TODO(bca): User mutations should be handled in a the data layer and
      //            new data should be received as updated props.
      this.setState({ networks: getSocialNetworkState(user) });
    },
  );

  forceTextFieldValue(value: string) {
    if (this.props.value) {
      this.previousValue = this.props.value;
    }
    this.props.onChange(value);
  }

  restoreTextFieldValue() {
    this.props.onChange(this.previousValue);
    delete this.previousValue;
  }

  logTypeaheadInput = (value: string) => {
    track(PCONST.thrift.EventType.SEARCH_SOCIAL_TYPEAHEAD, {
      component: PCONST.thrift.ComponentType.SOCIAL_TYPEAHEAD_SUGGESTIONS,
      element: PCONST.thrift.ElementType.SEARCH_CONTACT_INPUT,
      entered_query: value,
    });
  };

  logSearchContactItemClick = (contactItemIndex: number) => {
    const { value } = this.props;
    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.SOCIAL_TYPEAHEAD_SUGGESTIONS,
      element: PCONST.thrift.ElementType.SEARCH_CONTACT_LIST_ITEM,
      entered_query: value,
      result_index: contactItemIndex,
    });
  };

  renderResultItems = () => {
    const { changeOnTextHover, inviteQueue, isWebMessagingRedesignExpEnabled } = this.props;
    const resultsClassName = cx(
      'Typeahead',
      'userSelect',
      { open: this.state.open },
      { webInboxRedesign: isWebMessagingRedesignExpEnabled },
    );
    const resultItems = this.state.typeaheadSuggestions
      .map((item, idx) => (
        <SendToUser
          key={idx}
          onClick={() => {
            this.onUserInviteClick(item);
            this.logSearchContactItemClick(idx);
          }}
          onHoverEnd={() => (changeOnTextHover ? this.restoreTextFieldValue() : null)}
          onHoverStart={() => (changeOnTextHover ? this.forceTextFieldValue(item.label) : null)}
          inviteQueue={inviteQueue}
          {...item}
        />
      ))
      .concat(this.getStaticItems())
      .map((item, idx) => <li key={idx}>{item}</li>);

    const containerStyle = {
      overflow: 'scroll',
      height: '364px',
      zIndex: 1000,
      marginTop: isWebMessagingRedesignExpEnabled ? 8 : 4,
      border: isWebMessagingRedesignExpEnabled ? '1px solid #dadada' : null,
      borderTop: 'none',
    };

    return (
      <div
        className={resultsClassName}
        ref={(r) => {
          this._results = r;
        }}
        style={containerStyle}
      >
        {resultItems}
      </div>
    );
  };

  render() {
    const {
      boardId,
      className,
      onChange,
      onKeyDown,
      showInputPlaceholder,
      value,
      isWebMessagingRedesignExpEnabled,
    } = this.props;

    const resourceOptions = boardId ? { board: boardId } : null;
    const placeholderString = i18n._('Enter name or email', 'search');
    return (
      <Typeahead
        onTypeaheadSuggestionsChange={this.handleTypeaheadSuggestionsChange}
        resourceName={'ShareSuggestionsTypeaheadResource'}
        resourceOptions={{ ...resourceOptions, count: 25 }}
        value={value || ''}
      >
        <div className={cx(className, 'reactSocialTypeahead')}>
          <div
            className={cx(
              'TypeaheadField',
              'userSelect',
              'inputContainer',
              'socialInboxTypeahead',
              { webInboxRedesign: isWebMessagingRedesignExpEnabled },
            )}
          >
            <TextField
              id="collaboratorInviterTypeahead"
              onChange={({ value }) => {
                onChange(value);
                this.logTypeaheadInput(value);
              }}
              onKeyDown={onKeyDown}
              placeholder={showInputPlaceholder ? placeholderString : ''}
              value={value}
            />
          </div>
          {this.renderResultItems()}
        </div>
      </Typeahead>
    );
  }
}

const mapStateToProps = (state) => {
  const viewer = state.viewer;
  return { viewer };
};

export default compose(
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  connect<Props, *, _, _, _, _>(mapStateToProps),
)(SocialTypeahead);
